// Global success feedback — admin-configurable ("pop-up machine").
// A big celebration pop-up or a small toast per action, controlled from
// Admin -> Success Pop-ups. Driven from axios interceptors (user + admin).
import { toast } from "sonner";

// Live config loaded from GET /api/popup-config (set on app start).
let CONFIG = null;
export function setPopupConfig(cfg) { CONFIG = cfg || null; }

// Fallback defaults (mirror backend) used until config loads.
const DEFAULTS = {
    enabled: true, title: "Success!", icon: "check", auto_dismiss_ms: 2600,
    triggers: {
        member: { message: "Family member saved to your Family Repository 🎉", mode: "celebrate" },
        upload: { message: "Added to your Family Repository ✓", mode: "celebrate" },
        request: { message: "Request submitted ✓", mode: "celebrate" },
        profile: { message: "Profile saved ✓", mode: "celebrate" },
        legacy: { message: "Saved to your family legacy ✓", mode: "celebrate" },
        invite: { message: "Invitation sent ✓", mode: "celebrate" },
        whatsapp_template: { message: "Template submitted to Meta ✓", mode: "toast" },
        integration: { message: "Integration saved ✓", mode: "toast" },
        invoice: { message: "Invoice details saved ✓", mode: "toast" },
        subscription: { message: "Your Family Repository plan is updated 🎉", mode: "celebrate" },
        site_asset: { message: "Site asset updated ✓", mode: "toast" },
        content: { message: "Content saved ✓", mode: "toast" },
        support: { message: "Thank you — the Family Repository team will get back to you ✓", mode: "toast" },
        reminder: { message: "Reminder set ✓", mode: "toast" },
        crm: { message: "Saved ✓", mode: "toast" },
    },
};

// url pattern -> trigger key (order: specific first).
const TRIGGERS = [
    { key: "whatsapp_template", re: /\/whatsapp\/templates/ },
    { key: "invoice", re: /\/invoice-settings/ },
    { key: "site_asset", re: /\/site-assets/ },
    { key: "integration", re: /\/integrations/ },
    { key: "support", re: /\/(tickets?|feedback|support-requests?)\b/ },
    { key: "reminder", re: /\/(reminders?|calendar\/events|birthdays)\b/ },
    { key: "invite", re: /\/invit/ },
    { key: "member", re: /\/(tree\/)?(persons|people|members?|relationships?)\b/ },
    { key: "upload", re: /\/(media|uploads?|photos?|gallery|memories|documents?|audio|video|voice|vault)\b/ },
    { key: "legacy", re: /\/(stories|story|capsule|legacy|culture|milestones?|wishes?|legal|wills?|nominees?|family-records?|records?|achievements?)\b/ },
    { key: "request", re: /\/requests?\b/ },
    { key: "subscription", re: /\/(package|subscription|plan)\b/ },
    { key: "profile", re: /\/(profile|account|users\/me|users\/profile|settings)\b/ },
    { key: "content", re: /\/(landing|content|faq|team|testimonial|templates?|coupons?|features?|demos?|banners?|nav-links?|footer|use-cases|slides?|hero-tree|hero-images?)\b/ },
    { key: "crm", re: /\/(leads?|opportunit|customers?|followups?|tasks?|contacts?|segments?|campaigns?|automations?|kb)\b/ },
];

const DENY = [
    /\/auth\b/, /\/otp\b/, /\/login\b/, /\/logout\b/, /\/refresh\b/, /\/verify\b/,
    /draft/i, /autosave/i, /\/track\b/, /\/view\b/, /\/presence\b/, /\/heartbeat\b/,
    /\/search\b/, /\/translate\b/, /\/audit\//, /\/read\b/, /\/seen\b/, /\/typing\b/,
    /\/receipt\.pdf/, /\/test\b/, /\/send-test\b/, /\/diagnose\b/, /popup-config/,
];

let lastKey = "";
let lastAt = 0;

function fire(mode, message, title, icon, dismiss) {
    const dk = mode + ":" + message;
    const now = Date.now();
    if (dk === lastKey && now - lastAt < 1500) return;
    lastKey = dk;
    lastAt = now;
    if (mode === "celebrate" && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("vt:celebrate", { detail: { message, title, icon, dismiss } }));
    } else if (mode === "toast") {
        toast.success(message, { id: "done:" + message });
    }
}

// Manual celebration (used by feedback + can be imported anywhere).
export function celebrate(message) {
    const c = CONFIG || DEFAULTS;
    fire("celebrate", message, c.title, c.icon, c.auto_dismiss_ms);
}

export function autoNotify(config, status) {
    try {
        if (!config || config.silentSuccess) return;
        const method = (config.method || "get").toLowerCase();
        if (!["post", "put", "patch", "delete"].includes(method)) return;
        if (status < 200 || status >= 300 || status === 202) return;
        const url = (config.url || "").split("?")[0];
        if (!url || DENY.some((r) => r.test(url))) return;
        const c = CONFIG || DEFAULTS;
        if (c.enabled === false) return;
        const hit = TRIGGERS.find((t) => t.re.test(url));
        if (!hit) return;
        const t = (c.triggers || {})[hit.key] || DEFAULTS.triggers[hit.key];
        if (!t || t.mode === "off") return;
        fire(t.mode, t.message || "Done ✓", c.title, c.icon, c.auto_dismiss_ms);
    } catch {
        /* never let feedback break a request */
    }
}
